import { useRef, useCallback, useContext } from 'preact/hooks';
import { ICONS } from '../utils/constants.js';
import { ChatContext } from '../hooks/context.js';
import { isLoading } from '../store/chatStore.js';

export function InputBox({ onSend, placeholder, isFloatpanel, extraChildren }) {
  const { cfg, bus, session } = useContext(ChatContext);
  const inputRef = useRef(null);

  const resizeInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, cfg.inputMaxRows * 22)}px`;
  }, [cfg.inputMaxRows]);

  const handleInput = useCallback(() => {
    resizeInput();
  }, [resizeInput]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const val = inputRef.current?.value?.trim();
      if (val && !isLoading.value) {
        submit();
      }
    }
  }, []);

  const submit = useCallback(() => {
    const val = inputRef.current?.value?.trim();
    if (!val || isLoading.value) return;
    inputRef.current.value = '';
    inputRef.current.style.height = '';
    onSend(val);
  }, [onSend]);

  const handleSend = useCallback(() => submit(), [submit]);

  const handleAttach = useCallback(() => {
    bus.emit('action', { action: 'attach', sessionId: session.getOrCreate().sessionId });
  }, [bus, session]);

  // Expose focus method
  InputBox._focus = () => inputRef.current?.focus();

  const wrapClass   = isFloatpanel ? 'mc-ginput-wrap' : 'mc-input-area';
  const rowClass    = isFloatpanel ? 'mc-ginput-row'  : 'mc-input-row';
  const inputPhdr   = isFloatpanel ? (cfg.floatpanelPlaceholder || cfg.placeholder) : cfg.placeholder;

  return (
    <div class={wrapClass} part="input-area">
      <div class={rowClass}>
        {cfg.allowAttachments && (
          <button class="mc-attach-btn" title="Attach file" aria-label="Attach file"
            onClick={handleAttach} dangerouslySetInnerHTML={{ __html: ICONS.attach }} />
        )}
        <textarea
          ref={inputRef}
          class="mc-input"
          placeholder={placeholder || inputPhdr}
          rows={1}
          aria-label="Type a message"
          maxLength={4000}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
        />
        {extraChildren}
        <button
          class="mc-send-btn"
          aria-label="Send message"
          onClick={handleSend}
          dangerouslySetInnerHTML={{ __html: ICONS.send }}
        />
      </div>
    </div>
  );
}
